import React, { useState, useCallback, useEffect, useRef } from "react";

import { ReducersTypes } from "../redux/reducers/Models";
import { Link } from "react-router-dom";
import Icon from "@/atomic/atoms/Icon";
import Loading from "@/atomic/atoms/Loading";
import LoadingOverlay from "@/atomic/atoms/LoadingOverlay";
import LoginModal from "@/atomic/atoms/LoginModal";
import Modal from "@/atomic/atoms/Modal";
import LanguageSelector from "@/atomic/atoms/LanguageSelector";
import AccordionList from "@/atomic/atoms/AccordionList";
import WebViewMenu from "@/atomic/molecules/WebViewMenu";
import WebViewHeader from "@/atomic/molecules/WebViewHeader";
import WhatsAppHomeButton from "@/atomic/atoms/WhatsAppHomeButton";
import { closeWebView } from "@/hooks/useIsWebView";
import environment from "@/settings/environment";
import useI18n from "@/hooks/useI18n";
import { faqInfos } from "./faq/constants/faqInfos";
import { InputMask } from "@react-input/mask";
import carroSemparar from "@/assets/carro-semparar.png";

const featureItems = [
  {
    title: "Como funciona",
    description:
      "A tecnologia dos pórticos identifica o veículo, entende seu tipo e número de eixos e faz a cobrança automaticamente.",
  },
  {
    title: "Clientes Sem Parar",
    description:
      "Quem tem tag recebe a cobrança automaticamente. Quem não tem, precisa fazer o pagamento manualmente.",
  },
  {
    title: "Limite de velocidade",
    description:
      "Basta seguir os limites de velocidade de cada trecho da rodovia.",
  },
  {
    title: "Quem já tem a tag precisa trocar por uma nova versão?",
    description:
      "Não. A tag Sem Parar, assim como outras tags de pagamento automático, já funciona no Free Flow.",
  },
];

const landingMobileFaqItems = faqInfos.slice(0, 7);

function CheckIcon({ className }: { className?: string }) {
  return (
    <div
      className={`bg-primary flex items-center justify-center rounded-lg shrink-0 size-10 md:size-14 ${className || ""}`}
    >
      <svg
        className="size-[22px] md:size-6"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
          fill="white"
        />
      </svg>
    </div>
  );
}

export interface PropsView {
  toggleMenu: () => void;
  isVisibleMenu: boolean;
  user: ReducersTypes["userReducer"];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  goToDebts: (e: React.ChangeEvent<HTMLFormElement>) => void;
  isDisabled: boolean;
  isLoading: boolean;
  error: boolean;
  isVisibleModal: boolean;
  toggleModal: () => void;
  isVisibleModalLogin: boolean;
  toggleModalLogin: () => void;
  goToCreateAccount: () => void;
  isCaptchaVerified: boolean;
  isCaptchaMtcCaptcha: boolean;
  skipCaptchaValidation?: boolean;
  isLoadingCaptchaFlag?: boolean;
  /** Indica se está rodando dentro de uma WebView */
  isWebView?: boolean;
  /** Erros de validação dos campos */
  errors?: {
    email?: string;
    phone?: string;
  };
}

export default function IndexView({
  toggleMenu,
  isVisibleMenu,
  user,
  onChange,
  goToDebts,
  isDisabled,
  isLoading,
  error,
  isVisibleModal,
  toggleModal,
  isVisibleModalLogin,
  toggleModalLogin,
  goToCreateAccount,
  isCaptchaVerified,
  isCaptchaMtcCaptcha,
  skipCaptchaValidation = false,
  isLoadingCaptchaFlag = false,
  isWebView = false,
  errors = {},
}: PropsView) {
  const { t, Trans } = useI18n();
  const [isWebViewMenuOpen, setIsWebViewMenuOpen] = useState(false);
  const [showDesktopNav, setShowDesktopNav] = useState(false);
  const [activeDesktopSection, setActiveDesktopSection] = useState<
    "about" | "how-it-works" | "highways" | "benefits"
  >("about");
  const [aboutScrollProgress, setAboutScrollProgress] = useState(0);
  const [showPlans, setShowPlans] = useState(false);
  const aboutDesktopRef = useRef<HTMLDivElement | null>(null);

  const handleOpenWebViewMenu = useCallback(() => {
    setIsWebViewMenuOpen(true);
  }, []);

  const handleCloseWebViewMenu = useCallback(() => {
    setIsWebViewMenuOpen(false);
  }, []);

  const handleLoginClick = useCallback(() => {
    toggleModalLogin();
  }, [toggleModalLogin]);

  const handleGoBack = useCallback(() => {
    closeWebView();
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const headerH =
      window.innerWidth >= 1280 ? 130 : window.innerWidth >= 768 ? 100 : 85;
    const top = el.getBoundingClientRect().top + window.scrollY - headerH;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  const handleMenuNavClick = useCallback(
    (sectionId: string) => {
      toggleMenu();
      setTimeout(() => scrollToSection(sectionId), 100);
    },
    [toggleMenu, scrollToSection],
  );

  const handleDesktopNavClick = useCallback(
    (e: React.MouseEvent, sectionId: string) => {
      e.preventDefault();
      scrollToSection(sectionId);
    },
    [scrollToSection],
  );

  const showWebViewMenu = isWebView;

  useEffect(() => {
    if (isVisibleMenu && window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isVisibleMenu]);

  useEffect(() => {
    const updateDesktopNavVisibility = () => {
      if (window.innerWidth < 1280) {
        setShowDesktopNav(false);
        return;
      }

      const secondSection = document.getElementById("about");
      if (!secondSection) return;

      const triggerOffset =
        secondSection.getBoundingClientRect().top + window.scrollY - 120;
      setShowDesktopNav(window.scrollY >= triggerOffset);

      const sectionIds: Array<
        "about" | "how-it-works" | "highways" | "benefits"
      > = ["about", "how-it-works", "highways", "benefits"];
      const headerOffset = 160;
      let currentSection: "about" | "how-it-works" | "highways" | "benefits" =
        "about";

      for (const sectionId of sectionIds) {
        const sectionEl = document.getElementById(sectionId);
        if (!sectionEl) continue;

        const sectionTop =
          sectionEl.getBoundingClientRect().top + window.scrollY;
        if (window.scrollY >= sectionTop - headerOffset) {
          currentSection = sectionId;
        }
      }

      setActiveDesktopSection(currentSection);

      if (window.innerWidth >= 1280 && aboutDesktopRef.current) {
        const aboutRect = aboutDesktopRef.current.getBoundingClientRect();
        const scrollableDistance = Math.max(
          aboutRect.height - window.innerHeight,
          1,
        );
        const sectionProgress = Math.min(
          Math.max(-aboutRect.top / scrollableDistance, 0),
          1,
        );
        setAboutScrollProgress(sectionProgress);
      }
    };

    updateDesktopNavVisibility();
    window.addEventListener("scroll", updateDesktopNavVisibility, {
      passive: true,
    });
    window.addEventListener("resize", updateDesktopNavVisibility);

    return () => {
      window.removeEventListener("scroll", updateDesktopNavVisibility);
      window.removeEventListener("resize", updateDesktopNavVisibility);
    };
  }, []);

  const getMessage = () => {
    if (error) return t("pages:welcome:modalMessageDebtsNotFound");
    return t("pages:welcome:modalMessagePaymentAlreadyAutomated");
  };

  const aboutCarRotation =
    aboutScrollProgress > 0.33 && aboutScrollProgress < 0.66 ? 5 : 0;
  const aboutRoadOffset = aboutScrollProgress * 1400;
  const aboutCarPulse = Math.sin(aboutScrollProgress * Math.PI * 7) * 0.8;
  const aboutCarScale = 1 + Math.sin(aboutScrollProgress * Math.PI * 6) * 0.008;
  const aboutCarForwardLean =
    aboutScrollProgress > 0.33 && aboutScrollProgress < 0.66 ? -2 : 0;
  const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);
  const frame1Progress = clamp(aboutScrollProgress / 0.34, 0, 1);
  const frame2Progress = clamp((aboutScrollProgress - 0.33) / 0.34, 0, 1);
  const frame3Progress = clamp((aboutScrollProgress - 0.66) / 0.34, 0, 1);

  return (
    <div
      className={`relative w-full h-full min-h-screen flex flex-col ${isVisibleMenu ? "overflow-hidden" : ""}`}
    >
      <main className="relative w-full flex-1 flex flex-col md:min-h-screen bg-brand-dark bg-[url('./assets/home-desk-2x.png')] bg-no-repeat bg-cover bg-top xl:bg-fixed xl:bg-center">
        {/* Desktop white left-half panel */}
        <div className="hidden xl:block absolute top-0 left-0 bottom-0 w-1/2 bg-white pointer-events-none z-0" />

        {showWebViewMenu && (
          <WebViewHeader
            onOpenMenu={handleOpenWebViewMenu}
            onGoBack={handleGoBack}
          />
        )}

        <header
          className={`xl:hidden top-0 z-[1001] flex items-center w-full h-[75px] md:h-[90px] ${isVisibleMenu ? "md:fixed md:left-0 md:right-0 md:bg-brand-dark" : ""}`}
        >
          {!showWebViewMenu && (
            <button
              onClick={toggleMenu}
              type="button"
              className="flex items-center justify-center w-[75px] h-[75px] md:w-[90px] md:h-[90px] shrink-0 bg-brand-dark md:bg-transparent border-none outline-none appearance-none focus:outline-none"
              aria-controls="navbar-default"
              aria-expanded={isVisibleMenu}
            >
              {isVisibleMenu ? (
                <img
                  src="/svg/icon-close-white-2.0.svg"
                  alt="menu close"
                  className="w-[29px] h-[29px]"
                />
              ) : (
                <img
                  src="/svg/icon-menu-white-2.0.svg"
                  alt="menu"
                  className="w-[22px] h-[17px]"
                />
              )}
            </button>
          )}
          <div className="mx-6">
            <Icon className="dark" width="130" height="63" />
          </div>
          <div className="ml-auto mr-3">
            <LanguageSelector className="text-white" showFlag={true} />
          </div>
        </header>

        {/* --- Desktop header --- */}
        <header
          className={`hidden xl:flex ${showDesktopNav || isVisibleMenu ? "fixed top-0 left-0 right-0" : "absolute top-0 left-0 right-0"} w-full h-[120px] items-center z-[1001] transition-colors duration-200 ${showDesktopNav || isVisibleMenu ? "border-b border-[#d9d9d9] bg-white/90 backdrop-blur-md" : "border-b border-white/10 bg-brand-dark/30"}`}
        >
          {!showWebViewMenu && (
            <button
              onClick={toggleMenu}
              type="button"
              className="flex items-center justify-center w-[120px] h-[120px] shrink-0 bg-brand-dark border-none outline-none appearance-none focus:outline-none"
              aria-controls="navbar-default"
              aria-expanded={isVisibleMenu}
            >
              {isVisibleMenu ? (
                <img
                  src="/svg/icon-close-white-2.0.svg"
                  alt="menu close"
                  className="w-[29px] h-[29px]"
                />
              ) : (
                <img
                  src="/svg/icon-menu-white-2.0.svg"
                  alt="menu"
                  className="w-[30px] h-[22px]"
                />
              )}
            </button>
          )}
          <div className="ml-6">
            <img
              src="/images/logo-pedagio-black.svg"
              alt="Pedágio Eletrônico Sem Parar"
              width="150"
              height="46"
            />
          </div>
          <nav
            className={`ml-12 h-full transition-opacity duration-200 hidden xl:block ${showDesktopNav || isVisibleMenu ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <ul className="flex items-center gap-10 h-full">
              <li className="relative h-full flex items-center">
                <a
                  href="#about"
                  onClick={(e) => handleDesktopNavClick(e, "about")}
                  className={`font-secondary text-sm tracking-[0.2px] ${activeDesktopSection === "about" ? "text-[#111111]" : "text-[#535353]"}`}
                >
                  {t("navigation:aboutToll")}
                </a>
                {activeDesktopSection === "about" && (
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-[5px] w-[37px] h-[10px] bg-primary rounded-full" />
                )}
              </li>
              <li className="relative h-full flex items-center">
                <a
                  href="#how-it-works"
                  onClick={(e) => handleDesktopNavClick(e, "how-it-works")}
                  className={`font-secondary text-sm tracking-[0.2px] ${activeDesktopSection === "how-it-works" ? "text-[#111111]" : "text-[#535353]"}`}
                >
                  {t("navigation:howItWorks")}
                </a>
                {activeDesktopSection === "how-it-works" && (
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-[5px] w-[37px] h-[10px] bg-primary rounded-full" />
                )}
              </li>
              <li className="relative h-full flex items-center">
                <a
                  href="#highways"
                  onClick={(e) => handleDesktopNavClick(e, "highways")}
                  className={`font-secondary text-sm tracking-[0.2px] ${activeDesktopSection === "highways" ? "text-[#111111]" : "text-[#535353]"}`}
                >
                  {t("navigation:highways")}
                </a>
                {activeDesktopSection === "highways" && (
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-[5px] w-[37px] h-[10px] bg-primary rounded-full" />
                )}
              </li>
              <li className="relative h-full flex items-center">
                <a
                  href="#benefits"
                  onClick={(e) => handleDesktopNavClick(e, "benefits")}
                  className={`font-secondary text-sm tracking-[0.2px] ${activeDesktopSection === "benefits" ? "text-[#111111]" : "text-[#2b2b2b]"}`}
                >
                  {t("navigation:tagBenefits")}
                </a>
                {activeDesktopSection === "benefits" && (
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-[5px] w-[37px] h-[10px] bg-primary rounded-full" />
                )}
              </li>
            </ul>
          </nav>
          <div className="ml-auto flex items-center gap-[18px] mr-6">
            <LanguageSelector
              className={
                showDesktopNav || isVisibleMenu
                  ? "text-[#2b2b2b]"
                  : "text-white"
              }
              arrowImg={
                showDesktopNav || isVisibleMenu
                  ? "/arrow-bottom-black.svg"
                  : "/arrow-bottom-white.svg"
              }
              showFlag={true}
            />
            <button
              className={`rounded-[12px] h-[50px] w-[180px] font-bold text-sm tracking-[0.3px] font-secondary border ${showDesktopNav || isVisibleMenu ? "border-black/60 text-brand-dark" : "border-white/40 text-white"}`}
              onClick={goToCreateAccount}
            >
              {t("navigation:createAccount")}
            </button>
          </div>
        </header>

        <article className="mt-36 md:mt-16 flex-1 flex p-3 md:px-8 justify-center items-center xl:items-start xl:justify-between flex-col xl:flex-row xl:max-w-[1320px] xl:mx-auto xl:mt-[67px] xl:px-0 w-full relative z-[1]">
          <div className="hidden xl:block w-full flex-col text-left max-w-[500px] xl:pl-[84px] xl:pt-[217px]">
            <h1 className="text-[50px] font-semibold leading-[47px] text-[#0e0d0d] tracking-[-0.4px]">
              <Trans
                i18nKey="pages:welcome:heroSectionTitle"
                components={[<span className="text-secondary"></span>]}
              />
            </h1>
            <h2 className="text-lg font-medium text-[#0e0d0d] mt-4 max-w-[352px] leading-[1.3] tracking-[-0.4px]">
              {t("pages:welcome:heroSectionSubtitle")}
            </h2>
          </div>
          <form
            className="w-full md:max-w-[480px] xl:max-w-[420px] flex justify-start items-center flex-col gap-[10px] xl:gap-2 rounded-[20px] border border-white/15 bg-[rgba(105,102,102,0.1)] backdrop-blur-[10px] px-6 py-5 xl:p-10 min-h-96 mt-4 xl:mt-[120px] xl:mr-[85px] font-secondary"
            onSubmit={isDisabled ? () => null : goToDebts}
          >
            <p className="w-full text-white text-sm leading-[18.2px] tracking-[-0.4px] font-secondary font-normal xl:hidden">
              {t("pages:welcome:labelTitle")}
            </p>
            <input
              value={(user?.plate as string) ?? ""}
              name="plate"
              placeholder={t("pages:welcome:placeholderPlate")}
              className="w-full h-12 rounded-theme-medium border border-white bg-foreground px-3.5 py-3 text-text placeholder:text-text-secondary font-primary text-sm"
              onChange={onChange}
              type="text"
              minLength={5}
              maxLength={7}
            />
            <div className="w-full">
              <input
                value={(user?.email as string) ?? ""}
                name="email"
                placeholder={t("pages:welcome:placeholderEmail")}
                className={`w-full h-12 rounded-theme-medium border px-3.5 py-3 text-text placeholder:text-text-secondary font-primary text-sm ${errors.email ? "border-red-500" : "border-white"} bg-foreground`}
                onChange={onChange}
                type="email"
                inputMode="email"
                required
              />
              {errors.email && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.email}
                </span>
              )}
            </div>
            <div className="w-full">
              <InputMask
                value={(user?.phone as string) ?? ""}
                name="phone"
                placeholder={t("pages:welcome:placeholderPhone")}
                className={`w-full h-12 rounded-theme-medium border px-3.5 py-3 text-text placeholder:text-text-secondary font-primary text-sm ${errors.phone ? "border-red-500" : "border-white"} bg-foreground`}
                onChange={onChange}
                type="text"
                inputMode="numeric"
                mask="(__) _____-____"
                replacement={{ _: /\d/ }}
                required
              />
              {errors.phone && (
                <span className="text-red-500 text-xs mt-1">
                  {errors.phone}
                </span>
              )}
            </div>
            <div className="w-full">
              <p className="text-[10px] text-white leading-[14px] tracking-[-0.4px] font-secondary">
                <Trans
                  i18nKey="pages:welcome:labelTermsAndPrivacy"
                  components={[
                    <Link
                      to="/terms"
                      className="underline decoration-solid text-white"
                    />,
                    <Link
                      to="/policy"
                      className="underline decoration-solid text-white"
                    />,
                  ]}
                />
              </p>
            </div>
            {!skipCaptchaValidation && (
              <div className="w-full py-2 relative">
                <LoadingOverlay
                  isLoading={isLoadingCaptchaFlag}
                  bgColor="bg-foreground/40"
                />
                {isCaptchaMtcCaptcha ? (
                  <div
                    className="mtcaptcha"
                    id="mtcaptcha-intern"
                    data-callback="onCaptchaSuccess"
                  ></div>
                ) : (
                  <div
                    className="g-recaptcha mx-auto"
                    data-sitekey={environment.recaptcha.siteKey}
                    data-size="normal"
                    data-theme="light"
                    data-callback="onRecaptchaVerify"
                    data-expired-callback="onRecaptchaExpired"
                    data-error-callback="onRecaptchaError"
                  />
                )}
              </div>
            )}
            <button
              type="submit"
              className={`w-full h-[60px] text-white font-secondary text-base tracking-[-0.4px] px-5 py-4 rounded-[10px] transition-opacity ${isDisabled ? "bg-disabled text-disabled-text cursor-not-allowed" : "bg-primary hover:opacity-95"}`}
              disabled={isDisabled}
            >
              {t("pages:welcome:searchDebts")}
            </button>
            <div className="flex items-center justify-center h-[60px] gap-1 text-sm text-white font-secondary xl:flex-col xl:h-auto xl:gap-0.5 xl:pt-2">
              <span className="hidden xl:block text-white text-sm font-normal">
                Você ainda não tem uma conta?
              </span>
              <div className="contents xl:flex xl:items-center xl:gap-1">
                <button
                  type="button"
                  className="underline decoration-solid font-bold xl:no-underline xl:text-primary"
                  onClick={toggleModalLogin}
                >
                  {t("navigation:login")}
                </button>
                <span className="text-white font-normal">
                  {t("pages:welcome:orSeparator")}
                </span>
                <button
                  type="button"
                  className="underline decoration-solid font-bold xl:no-underline xl:text-primary"
                  onClick={goToCreateAccount}
                >
                  {t("navigation:createAccount")}
                </button>
              </div>
            </div>
          </form>
        </article>

        {/* Scroll down button — desktop only */}
        <button
          type="button"
          className="hidden xl:flex absolute bottom-16 left-1/2 -translate-x-1/2 items-center justify-center w-14 h-14 bg-primary rounded-lg z-10"
          onClick={() => {
            const sections = document.querySelector("[data-sections]");
            sections?.scrollIntoView({ behavior: "smooth" });
          }}
          aria-label="Scroll down"
        >
          <svg
            width="16"
            height="20"
            viewBox="0 0 16 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 1v18M1 12l7 7 7-7"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </main>

      {/* ======= Content Sections ======= */}
      <div className="relative w-full" data-sections>
        {/* Car transition — mobile only */}
        <div className="xl:hidden relative w-full">
          <div className="absolute inset-0 top-0 bottom-1/2 bg-brand-dark" />
          <div className="absolute inset-0 top-1/2 bottom-0 bg-[#e6e6e6]" />
          <div className="relative z-10 flex justify-center">
            <img
              src={carroSemparar}
              alt=""
              className="w-[260px] md:w-[320px] h-auto"
            />
          </div>
        </div>

        {/* O que é Pedágio Eletrônico */}
        <section
          id="about"
          className="bg-[#e6e6e6] w-full pt-12 pb-16 overflow-hidden xl:overflow-visible"
        >
          {/* --- Mobile layout --- */}
          <div className="xl:hidden mx-auto px-4 md:px-8 md:max-w-3xl">
            <div className="mb-2">
              <p className="font-myFont text-[50px] uppercase text-brand-dark font-normal mb-[-7px]">
                o que é
              </p>
              <p className="font-myFont text-[50px] font-black uppercase leading-[1.1] text-primary mb-[-7px]">
                PEDÁGIO
              </p>
              <p className="font-myFont text-[50px] font-black uppercase leading-[1.1] text-primary">
                ELETRÔNICO
              </p>
            </div>
            <p className="font-myFont text-base leading-[1.3] tracking-[-0.4px] text-[#525252]">
              É um sistema de pedágio que identifica o veículo em movimento e
              faz a cobrança automaticamente, sem precisar parar ou reduzir a
              velocidade.
            </p>
            <div className="mt-6 mb-6">
              <img
                src="/images/portal-icon.svg"
                alt=""
                className="w-[59px] h-[53px] mb-4"
              />
              <p className="font-myFont text-base leading-[1.3] tracking-[-0.4px] text-[#525252]">
                Alguns pontos da rodovia recebem pórticos com câmeras e sensores
                que identificam os veículos em movimento.
              </p>
              <p className="font-myFont font-bold text-base leading-[1.3] tracking-[-0.4px] text-[#525252] mt-4">
                Não há praças de pedágio nem atendimento presencial nesses
                locais.
              </p>
            </div>
            <div className="flex flex-col gap-11 ml-2 md:grid md:grid-cols-2 md:gap-8 md:ml-0">
              {featureItems.map((item, index) => (
                <div key={index} className="flex flex-col gap-5">
                  <CheckIcon />
                  <div className="flex flex-col gap-2 tracking-[-0.4px]">
                    <h3 className="font-myFont font-bold text-[22px] leading-[1.3] text-brand-dark">
                      {item.title}
                    </h3>
                    <p className="font-myFont font-normal text-base leading-[1.3] text-[#525252]">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* --- Desktop layout split in 3 Figma frames with one moving car --- */}
          <div
            ref={aboutDesktopRef}
            className="hidden xl:block relative max-w-[1440px] mx-auto h-[2700px]"
          >
            {/* Continuous road center line */}
            <div
              className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[16px] opacity-35 pointer-events-none"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(to bottom, #c9c9c9 0, #c9c9c9 28px, transparent 28px, transparent 58px)",
                backgroundPositionY: `${aboutRoadOffset}px`,
              }}
            />

            {/* Single sticky car: content passes while car "moves forward" */}
            <div className="sticky top-[120px] h-[calc(100vh-120px)] z-[40] pointer-events-none">
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div
                  className="absolute top-[130px] left-1/2 -translate-x-1/2 w-[106px] h-[349px] rounded-[112px] bg-black/25 blur-[35px]"
                  style={{ transform: `translateY(${aboutCarPulse}px)` }}
                />
                <div
                  className="absolute top-[122px] left-1/2 -translate-x-1/2 w-[113px] h-[370px] rounded-[112px] bg-black/22 blur-[35px]"
                  style={{ transform: `translateY(${aboutCarPulse * 1.2}px)` }}
                />
                <img
                  src={carroSemparar}
                  alt="Veículo visto de cima"
                  className="relative w-[250px] h-auto object-contain transition-transform duration-200 will-change-transform"
                  style={{
                    transform: `translateY(${aboutCarPulse}px) scale(${aboutCarScale}) rotate(${aboutCarRotation + aboutCarForwardLean}deg)`,
                  }}
                />
              </div>
            </div>

            {/* Frame 1 content */}
            <div className="absolute left-0 top-0 h-[900px] w-full overflow-hidden z-[10]">
              <div
                className="absolute right-[calc(50%+190px)] top-[368px] flex flex-col items-end text-right gap-[30px] w-[370px]"
                style={{
                  transform: `translateY(${-frame1Progress * 170}px) scale(${1 - frame1Progress * 0.04})`,
                  opacity: 1 - frame1Progress * 0.2,
                }}
              >
                <img
                  src="/images/portal-icon.svg"
                  alt=""
                  className="w-[59px] h-[53px]"
                />
                <p className="font-myFont text-[18px] leading-[1.3] tracking-[-0.4px] text-brand-dark">
                  São escolhidos alguns{" "}
                  <strong>
                    pontos na rodovia para a instalação de pórticos equipados
                    com câmeras e sensores
                  </strong>{" "}
                  que fazem a{" "}
                  <strong className="text-primary">
                    leitura das placas e características dos veículos
                  </strong>
                  .
                </p>
                <p className="font-myFont font-medium text-[18px] leading-[1.3] tracking-[-0.4px] text-brand-dark">
                  Esses pontos não são rodeados por praças de pedágio
                  convencionais e funcionários presenciais.
                </p>
              </div>
              <div
                className="absolute left-[calc(50%+190px)] top-[361px] w-[420px]"
                style={{
                  transform: `translateY(${-frame1Progress * 140}px) scale(${1 - frame1Progress * 0.03})`,
                  opacity: 1 - frame1Progress * 0.16,
                }}
              >
                <p className="font-myFont text-[70px] font-black uppercase leading-[0.87] tracking-[-0.4px] text-brand-dark">
                  O QUE É
                </p>
                <p className="font-myFont text-[70px] font-black uppercase leading-[0.87] tracking-[-0.4px] text-primary">
                  PEDÁGIO
                  <br />
                  ELETRÔNICO
                </p>
                <p className="font-myFont font-medium text-base leading-[1.3] tracking-[-0.4px] text-[#525252] w-[396px] mt-6">
                  É um sistema de pedágio que permite que os veículos em
                  passagem por uma rodovia sejam identificados e tarifados sem
                  que haja a necessidade de parar ou sequer reduzir a sua
                  velocidade.
                </p>
              </div>
            </div>

            {/* Frame 2 content */}
            <div className="absolute left-0 top-[900px] h-[900px] w-full overflow-hidden z-[10]">
              <div
                className="absolute right-[calc(50%+190px)] top-[343px] w-[311px] text-right"
                style={{
                  transform: `translateY(${-frame2Progress * 190}px) scale(${1 - frame2Progress * 0.035})`,
                  opacity: 1 - frame2Progress * 0.18,
                }}
              >
                <CheckIcon className="ml-auto" />
                <h3 className="font-myFont font-bold text-2xl leading-[1.3] tracking-[-0.4px] text-brand-dark mt-5">
                  Clientes Sem Parar
                </h3>
                <p className="font-myFont text-lg leading-[1.3] tracking-[-0.4px] text-[#525252] mt-4">
                  Recebe a cobrança de suas faturas automaticamente e clientes
                  sem tag precisam pagar a tarifa manualmente.
                </p>
              </div>
              <div
                className="absolute left-[calc(50%+190px)] top-[210px] w-[420px]"
                style={{
                  transform: `translateY(${-frame2Progress * 155}px) scale(${1 - frame2Progress * 0.03})`,
                  opacity: 1 - frame2Progress * 0.14,
                }}
              >
                <CheckIcon />
                <h3 className="font-myFont font-bold text-2xl leading-[1.3] tracking-[-0.4px] text-brand-dark mt-5">
                  Como funciona o Sistema
                </h3>
                <p className="font-myFont text-lg leading-[1.3] tracking-[-0.4px] text-[#525252] mt-4">
                  Por meio de pórticos com tecnologia que identifica e
                  classifica os veículos, e cobra a tarifa eletronicamente,
                  conforme o tipo e o número de eixos.
                </p>
              </div>
            </div>

            {/* Frame 3 content */}
            <div className="absolute left-0 top-[1800px] h-[900px] w-full overflow-hidden z-[10]">
              <div
                className="absolute right-[calc(50%+190px)] top-[287px] w-[311px] text-right"
                style={{
                  transform: `translateY(${-frame3Progress * 135}px) scale(${1 - frame3Progress * 0.02})`,
                  opacity: 1 - frame3Progress * 0.08,
                }}
              >
                <CheckIcon className="ml-auto" />
                <h3 className="font-myFont font-bold text-2xl leading-[1.3] tracking-[-0.4px] text-brand-dark mt-5">
                  Limite de velocidade
                </h3>
                <p className="font-myFont text-lg leading-[1.3] tracking-[-0.4px] text-[#525252] mt-4">
                  Os motoristas devem apenas respeitar os limites de velocidade
                  dos respectivos trechos das rodovias.
                </p>
              </div>
              <div
                className="absolute left-[calc(50%+190px)] top-[403px] w-[420px]"
                style={{
                  transform: `translateY(${-frame3Progress * 110}px) scale(${1 - frame3Progress * 0.02})`,
                  opacity: 1 - frame3Progress * 0.07,
                }}
              >
                <CheckIcon />
                <h3 className="font-myFont font-bold text-2xl leading-[1.3] tracking-[-0.4px] text-brand-dark mt-5">
                  Quem já tem a tag precisa trocar por uma nova versão?
                </h3>
                <p className="font-myFont text-lg leading-[1.3] tracking-[-0.4px] text-[#525252] mt-4">
                  Não. A tag Sem Parar e as outras tags de pagamento automático
                  são a forma mais completa e eficaz para aproveitar os
                  benefícios do free flow e não é necessário trocá-la ou
                  adquirir uma nova tag específica para essa tecnologia.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Veja um exemplo de como funciona (apenas desktop/tablet) */}
        <section
          id="how-it-works"
          className="hidden md:block bg-[#e6e6e6] w-full pt-[80px] pb-[80px] xl:pt-[96px] xl:pb-[132px]"
        >
          <div className="mx-auto max-w-[1240px] px-8 xl:px-10">
            <div className="mx-auto w-full max-w-[1036px]">
              <div className="flex flex-col items-center gap-[29px] leading-[0] text-center uppercase">
                <p className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[48px] xl:text-[60px] leading-[30px] tracking-[-0.4px] text-brand-dark whitespace-nowrap">
                  VEJA UM EXEMPLO DE
                </p>
                <p className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[64px] xl:text-[80px] leading-[30px] tracking-[-0.4px] text-primary whitespace-nowrap">
                  COMO FUNCIONA
                </p>
              </div>

              <div className="mt-[60px] flex flex-col gap-10 xl:mt-[60px] xl:flex-row xl:items-center xl:gap-[120px]">
                <div className="tracking-[-0.4px]">
                  <p className="font-['Hanken_Grotesk:Regular',sans-serif] text-[18px] xl:text-[22px] leading-[1.3] text-[#525252] w-full xl:w-[538px]">
                    <span className="font-normal">
                      Na primeira passagem pelo Pedágio Eletrônico,{" "}
                    </span>
                    <span className="font-bold text-primary">
                      você ganha até 5% de desconto{" "}
                    </span>
                    <span className="font-normal">
                      só por ter a tag Sem Parar Instalada.
                    </span>
                  </p>
                  <p className="mt-[30px] font-['Hanken_Grotesk:Regular',sans-serif] font-normal text-[18px] xl:text-[22px] leading-[1.3] text-[#525252] w-full xl:w-[556px]">
                    O prazo para pagamento é de até 30 dias, contados a partir
                    da data da passagem pelo pórtico. O não pagamento
                    caracteriza evasão de pedágio, infração grave prevista no
                    Código de Trânsito Brasileiro.
                  </p>
                  <p className="mt-[30px] font-['Hanken_Grotesk:Regular',sans-serif] font-normal text-[18px] xl:text-[22px] leading-[1.3] text-[#525252] w-full xl:w-[538px]">
                    Para maiores informações acesse:
                  </p>
                  <a
                    href="https://viaappia.com.br/sigafacil"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block font-['Hanken_Grotesk:SemiBold',sans-serif] font-semibold text-[20px] xl:text-[24px] leading-[1.3] text-primary"
                  >
                    viaappia.com.br/sigafacil
                  </a>
                </div>

                <div className="w-full max-w-[520px] mx-auto xl:mx-0 xl:max-w-[380px] bg-[#f9f9f9] rounded-[15px] px-6 pt-6 pb-7">
                  <p className="font-['Hanken_Grotesk:Black',sans-serif] font-black text-[18px] leading-normal tracking-[-0.18px] text-primary text-center">
                    Locais com Pedágio Eletrônico
                  </p>
                  <p className="mt-10 font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[18px] leading-normal tracking-[-0.18px] text-brand-dark text-center uppercase">
                    VIA SP SERRA
                  </p>

                  <div className="mt-10 space-y-[17px]">
                    <div className="flex items-end justify-between">
                      <p className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[16px] leading-[28px] text-[#525252] w-[128px]">
                        Extensão:
                      </p>
                      <div className="flex items-end gap-[10px]">
                        <span className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[60px] leading-[46px] text-brand-dark">
                          44
                        </span>
                        <span className="font-['Agenda:SemiboldExtraCondensed',sans-serif] text-[30px] leading-[46px] text-primary">
                          km
                        </span>
                      </div>
                    </div>

                    <div className="h-px bg-[#e9eaec]" />

                    <div className="flex items-end justify-between">
                      <p className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[16px] leading-[28px] text-[#525252] w-[128px]">
                        Cidades:
                      </p>
                      <span className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[60px] leading-[46px] text-brand-dark">
                        03
                      </span>
                    </div>

                    <div className="h-px bg-[#e9eaec]" />

                    <div className="flex items-center justify-between">
                      <p className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[16px] leading-[28px] text-[#525252] w-[128px]">
                        Rodovias:
                      </p>
                      <span className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[16px] leading-[28px] text-[#525252] text-right">
                        SP-021(Rodoanel)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quais locais contarão com Pedágio Eletrônico */}
        <section
          id="highways"
          className="bg-white w-full pt-12 pb-12 md:pt-[80px] xl:pt-[170px] md:pb-[80px] xl:pb-[88px]"
        >
          {/* Mobile mantém versão simplificada */}
          <div className="md:hidden mx-auto px-[23px]">
            <p className="font-myFont text-[45px] font-black uppercase leading-[0.98] text-brand-dark">
              QUAIS LOCAIS
              <br />
              CONTARÃO COM{" "}
              <span className="text-primary">PEDÁGIO ELETRÔNICO</span>
            </p>
            <p className="font-myFont text-[18px] leading-[1.08] tracking-[-0.4px] text-[#525252] mt-8 mb-8">
              <span>A nova modalidade de cobrança</span>
              <span className="font-bold">
                {" "}
                entrará em funcionamento na SP-021
              </span>
              <span>, no trecho norte do Rodoanel Mário Covas.</span>
            </p>
            <div className="mt-8 flex justify-center">
              <img
                src="/images/map-pedagio.png"
                alt="Mapa do pedágio eletrônico na SP-021"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* Desktop/Tablet pixel-perfect baseado no Figma 6258:29591 */}
          <div className="bg-white hidden md:flex mx-auto max-w-[1320px] px-8 xl:px-10 flex-row items-center justify-between gap-10">
            <div className="pt-[48px] w-full md:w-[40%] xl:w-[464px] shrink-0">
              <div className="font-['Hanken_Grotesk:ExtraBold',sans-serif] font-extrabold uppercase leading-[0] tracking-[-0.4px] text-[46px] xl:text-[55px]">
                <p className="leading-[38px] xl:leading-[60px] text-brand-dark">
                  quais locais
                </p>
                <p className="leading-[38px] xl:leading-[60px] text-brand-dark">
                  contarão com
                </p>
                <p className="leading-[38px] xl:leading-[60px] text-primary">
                  pedágio eletrônico
                </p>
              </div>
              <p className="mt-6 font-['Hanken_Grotesk:Regular',sans-serif] font-normal text-[18px] leading-[1.3] tracking-[-0.4px] text-[#525252] max-w-[452px]">
                A nova modalidade de cobrança{" "}
                <span className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold">
                  entrará em funcionamento na SP-021
                </span>
                , no trecho norte do Rodoanel Mario covas.
              </p>
            </div>

            <div className="w-full md:w-[55%] xl:max-w-none xl:w-[706px] shrink-0">
              <img
                src="/images/map-pedagio-1x.png"
                alt="Mapa com locais de pedágio eletrônico na SP-021"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </section>

        {/* Veja como funciona + texto de passagem (Figma: gray bg y=3185–3707) */}
        <section
          id="how-it-works-details"
          className="bg-[#e6e6e6] w-full pt-16 pb-[82px] md:hidden"
        >
          <div className="mx-auto px-[23px] md:max-w-5xl md:px-8">
            <p className="font-myFont text-[50px] font-black uppercase leading-[1] text-primary md:text-[70px]">
              VEJA COMO
              <br />
              FUNCIONA
            </p>
            <div className="flex flex-col gap-[16px] tracking-[-0.4px] mt-10 md:max-w-lg md:w-auto">
              <p className="font-myFont text-base leading-[1.3] text-brand-dark md:text-lg">
                <span>
                  Passou pela primeira vez no Pedágio Eletrônico com a tag Sem
                  Parar?
                </span>
                <span className="font-bold">
                  {" "}
                  Você já ganha até 5% de desconto.
                </span>
              </p>
              <p className="font-myFont text-base leading-[1.3] text-[#525252] md:text-lg">
                O prazo para pagamento é de até 30 dias, contados a partir da
                data da passagem pelo pórtico. O não pagamento caracteriza
                evasão de pedágio, infração grave prevista no Código de Trânsito
                Brasileiro.
              </p>
              <p className="font-myFont font-semibold text-base leading-[1.3] text-brand-dark">
                Para mais informações acesse:
                <br />
                <a
                  href="https://viaappia.com.br/sigafacil"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-2xl"
                >
                  viaappia.com.br/sigafacil
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Benefícios da Tag Sem Parar */}
        <section id="benefits" className="bg-white w-full overflow-hidden">
          {showPlans ? (
            <div className="py-12 px-[23px] md:px-8 xl:px-0 max-w-[1171px] mx-auto">
              <div className="flex items-center justify-between mb-10">
                <p className="font-myFont text-[22px] text-brand-dark leading-[1.3]">
                  Conheça nossos planos:
                </p>
                <button
                  type="button"
                  onClick={() => setShowPlans(false)}
                  className="p-1"
                  aria-label="Fechar"
                >
                  <svg
                    width="22"
                    height="23"
                    viewBox="0 0 22 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1.5L21 21.5"
                      stroke="#0e0d0d"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M21 1.5L1 21.5"
                      stroke="#0e0d0d"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
              <div
                data-testid="landing-plans-catalog"
                className="flex flex-col md:grid md:grid-cols-2 xl:flex xl:flex-row gap-4 xl:gap-[17px]"
              >
                {/* Livre Free Flow */}
                <div className="border border-primary rounded-t-[16px] rounded-b-[24px] bg-white flex flex-col xl:w-[280px] xl:shrink-0">
                  <div className="flex items-center gap-[10px] px-6 h-[60px] border-b border-[#e6e6e6] rounded-t-[16px]">
                    <span className="font-myFont font-semibold text-lg text-brand-dark flex-1 leading-none">
                      Livre Free Flow
                    </span>
                    <span className="bg-primary text-white text-xs font-bold px-2 rounded h-6 flex items-center justify-center whitespace-nowrap">
                      NOVIDADE
                    </span>
                  </div>
                  <div className="px-6 pt-6 pb-6 flex flex-col flex-1">
                    <p className="font-myFont font-semibold text-base text-brand-dark leading-[1.3]">
                      Pedágios eletrônicos
                    </p>
                    <div className="mt-3 flex flex-col gap-2">
                      <div>
                        <p className="font-myFont text-xs text-[#696966] leading-[1.3]">
                          Recarregue e use
                        </p>
                        <p className="font-myFont font-extrabold text-2xl text-brand-dark tracking-[-0.2px] leading-[1.2]">
                          Sem mensalidade
                        </p>
                      </div>
                      <span className="inline-flex items-center bg-[#363636] text-white text-xs font-semibold px-1 rounded h-[17px] self-start">
                        frete grátis
                      </span>
                    </div>
                    <div className="mt-6 flex flex-col gap-1">
                      <p className="font-myFont text-sm text-brand-dark leading-[1.5]">
                        Plano gratuito para uso em pedágios eletrônicos (Free
                        Flow).
                      </p>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Adesão grátis
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          5% de desconto no Free Flow
                        </span>
                      </div>
                    </div>
                    <div className="mt-auto pt-6">
                      <a
                        href="http://semparar.com.br/on/demandware.store/Sites-SemParar-Site/default/Cart-ExternalBuyNow?pid=004-LIVRE-MANUAL-FREEFLOW-PEDAGIOELETRONICO"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full h-[50px] bg-primary rounded-[12px] font-myFont font-bold text-lg text-white text-center uppercase"
                      >
                        PEDIR TAG
                      </a>
                    </div>
                  </div>
                </div>

                {/* Ultra */}
                <div className="border border-[#e6e6e6] rounded-t-[16px] rounded-b-[24px] bg-white flex flex-col xl:w-[280px] xl:shrink-0">
                  <div className="flex items-center gap-[10px] px-6 h-[60px] border-b border-[#e6e6e6] rounded-t-[16px] bg-white">
                    <span className="font-myFont font-semibold text-lg text-brand-dark flex-1 leading-none">
                      Ultra
                    </span>
                    <span className="bg-[#363636] text-white text-xs font-bold px-2 rounded h-6 flex items-center justify-center whitespace-nowrap">
                      Mais vendido
                    </span>
                  </div>
                  <div className="px-6 pt-6 pb-6 flex flex-col flex-1">
                    <p className="font-myFont font-semibold text-base text-brand-dark leading-[1.3]">
                      91% OFF por 10 meses
                    </p>
                    <div className="mt-3 flex flex-col gap-2">
                      <div>
                        <p className="font-myFont text-xs text-[#696966] leading-[1.3]">
                          De R$ 59,90 por
                        </p>
                        <p className="font-myFont text-brand-dark">
                          <span className="font-extrabold text-2xl tracking-[-0.2px] leading-[1.2]">
                            R$ 5,39
                          </span>
                          <span className="text-xs">/até o 10º mês</span>
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <span className="inline-flex items-center bg-[#363636] text-white text-xs font-semibold px-1 rounded h-[17px]">
                          Seguros inclusos
                        </span>
                        <span className="inline-flex items-center bg-[#363636] text-white text-xs font-semibold px-1 rounded h-[17px]">
                          frete grátis
                        </span>
                      </div>
                    </div>
                    <div className="mt-6 flex flex-col gap-1">
                      <p className="font-myFont text-sm text-brand-dark leading-[1.5]">
                        Use em pedágios e no Free Flow, ao abastecer, estacionar
                        e mais:
                      </p>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Assistência Guincho 24h
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Seguro Fatura Protegida
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Seguro Acidentes Pessoais
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Rastreio veicular e outros
                        </span>
                      </div>
                      <p className="font-myFont text-xs text-[#696966] leading-[1.4] mt-1">
                        Sem fidelidade e sem taxa.
                      </p>
                    </div>
                    <div className="mt-auto pt-6">
                      <a
                        href="http://semparar.com.br/on/demandware.store/Sites-SemParar-Site/default/Cart-ExternalBuyNow?pid=007-ETL-TURBINADO-IDEAL-91OFF-10M-PEDAGIOELETRONICO"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full h-[50px] border border-black rounded-[12px] font-myFont font-bold text-lg text-brand-dark text-center uppercase"
                      >
                        PEDIR TAG
                      </a>
                    </div>
                  </div>
                </div>

                {/* Conforto */}
                <div className="border border-[#e6e6e6] rounded-t-[16px] rounded-b-[24px] bg-white flex flex-col xl:w-[280px] xl:shrink-0">
                  <div className="flex items-center gap-[10px] px-6 h-[60px] border-b border-[#e6e6e6] rounded-t-[16px] bg-white">
                    <span className="font-myFont font-semibold text-lg text-brand-dark flex-1 leading-none">
                      Conforto
                    </span>
                  </div>
                  <div className="px-6 pt-6 pb-6 flex flex-col flex-1">
                    <p className="font-myFont font-semibold text-base text-brand-dark leading-[1.3]">
                      94% OFF por 10 meses
                    </p>
                    <div className="mt-3 flex flex-col gap-2">
                      <div>
                        <p className="font-myFont text-xs text-[#696966] leading-[1.3]">
                          De R$ 49,90 por
                        </p>
                        <p className="font-myFont text-brand-dark">
                          <span className="font-extrabold text-2xl tracking-[-0.2px] leading-[1.2]">
                            R$ 2,89
                          </span>
                          <span className="text-xs">/até o 10º mês</span>
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <span className="inline-flex items-center bg-[#363636] text-white text-xs font-semibold px-1 rounded h-[17px]">
                          Economize R$ 370
                        </span>
                        <span className="inline-flex items-center bg-[#363636] text-white text-xs font-semibold px-1 rounded h-[17px]">
                          frete grátis
                        </span>
                      </div>
                    </div>
                    <div className="mt-6 flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Pedágio e Free Flow
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Estacionamentos
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Abastecimentos
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Assistência Guincho 24h
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Seguro Fatura Protegida
                        </span>
                      </div>
                      <p className="font-myFont text-xs text-[#696966] leading-[1.4] mt-1">
                        Sem fidelidade e sem taxa.
                      </p>
                    </div>
                    <div className="mt-auto pt-6">
                      <a
                        href="http://semparar.com.br/on/demandware.store/Sites-SemParar-Site/default/Cart-ExternalBuyNow?pid=002-ETL-PRATICO-94OFF-10M-PEDAGIOELETRONICO"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full h-[50px] border border-black rounded-[12px] font-myFont font-bold text-lg text-brand-dark text-center uppercase"
                      >
                        PEDIR TAG
                      </a>
                    </div>
                  </div>
                </div>

                {/* Flex */}
                <div className="border border-[#e6e6e6] rounded-t-[16px] rounded-b-[24px] bg-white flex flex-col xl:w-[280px] xl:shrink-0">
                  <div className="flex items-center px-6 h-[60px] border-b border-[#e6e6e6] rounded-t-[16px] bg-white">
                    <span className="font-myFont font-semibold text-lg text-brand-dark leading-none">
                      Flex
                    </span>
                  </div>
                  <div className="px-6 pt-6 pb-6 flex flex-col flex-1">
                    <p className="font-myFont font-semibold text-base text-brand-dark leading-[1.3]">
                      Não usa, não paga!
                    </p>
                    <div className="mt-3 flex flex-col gap-2">
                      <div>
                        <p className="font-myFont text-xs text-[#696966] leading-[1.3]">
                          Por apenas
                        </p>
                        <p className="font-myFont text-brand-dark">
                          <span className="font-extrabold text-2xl tracking-[-0.2px] leading-[1.2]">
                            R$ 25,90
                          </span>
                          <span className="text-xs">/mês que usar</span>
                        </p>
                      </div>
                      <span className="inline-flex items-center bg-[#363636] text-white text-xs font-semibold px-1 rounded h-[17px] self-start">
                        frete grátis
                      </span>
                    </div>
                    <div className="mt-6 flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Pedágio e Free Flow
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="shrink-0 w-4 h-4"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3 8.5l3 3 7-7"
                            stroke="#00a868"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="font-myFont text-sm text-brand-dark leading-[1.5]">
                          Estacionamentos (2 dias/mês)
                        </span>
                      </div>
                    </div>
                    <div className="mt-auto pt-6">
                      <a
                        href="http://semparar.com.br/on/demandware.store/Sites-SemParar-Site/default/Cart-ExternalBuyNow?pid=003-FLEX-25-PEDAGIOELETRONICO"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full h-[50px] border border-black rounded-[12px] font-myFont font-bold text-lg text-brand-dark text-center uppercase"
                      >
                        PEDIR TAG
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile mantém versão simplificada */}
              <div className="md:hidden pt-12 pb-24">
                <div className="mx-auto px-[23px]">
                  <p className="font-myFont text-[45px] font-black uppercase leading-[0.98] text-brand-dark mb-0">
                    BENEFÍCIOS
                  </p>
                  <p className="font-myFont text-[45px] font-black uppercase leading-[0.98] text-brand-dark mb-0">
                    DA TAG
                  </p>
                  <p className="font-myFont text-[45px] font-black uppercase leading-[0.98] text-primary">
                    SEM PARAR
                  </p>
                  <p className="mt-[10px] font-myFont text-base tracking-[-0.4px] text-[#525252]">
                    <span>
                      Clientes Sem Parar e de outras tags de pagamento
                      automático ganham{" "}
                    </span>
                    <span className="font-bold">
                      5% de desconto em todas as passagens.
                    </span>
                  </p>
                </div>
                <div className="flex justify-center mt-[45px] mb-0">
                  <img
                    src="/images/beneficios-semparar.png"
                    alt="Tag Sem Parar"
                    className="w-auto h-auto max-w-full object-contain"
                  />
                </div>
                <div className="max-w-[360px] mx-auto px-[24px] mt-[-150px]">
                  <button
                    type="button"
                    className="w-[312px] h-[60px] bg-brand-dark text-[#f9f9f9] font-secondary font-medium text-sm tracking-[-0.4px] rounded-[12px]"
                    onClick={() => {
                      setShowPlans(true);
                      document
                        .getElementById("benefits")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    Conheça os planos
                  </button>
                </div>
              </div>

              {/* Desktop/Tablet com layout fluido */}
              <div className="hidden md:flex xl:block relative xl:min-h-[900px] max-w-[1440px] mx-auto items-start gap-6 px-8 pt-[80px] pb-[80px] xl:px-0 xl:pt-0 xl:pb-0">
                <div className="relative z-[2] flex-1 xl:mx-auto xl:max-w-[1205px] xl:pt-[308px] flex flex-col xl:flex-row items-start xl:justify-between gap-8 xl:gap-10">
                  <div className="w-full xl:w-auto">
                    <div className="flex flex-col gap-[6px] items-start font-['Hanken_Grotesk:ExtraBold',sans-serif] font-extrabold text-[46px] xl:text-[60px] leading-[38px] xl:leading-[48px] uppercase">
                      <p className="text-brand-dark">benefícios da</p>
                      <p className="text-primary">tag sem parar</p>
                    </div>
                    <p className="mt-6 font-['Hanken_Grotesk:Regular',sans-serif] text-[18px] xl:text-[20px] text-[#525252] max-w-[403px] tracking-[-0.4px] leading-[1.3]">
                      <span className="font-normal">{`Clientes Sem Parar e de outras Tags de pagamento automático `}</span>
                      <span className="font-bold">
                        ganham desconto de 5% em todas as passagens
                      </span>
                      <span className="font-normal">.</span>
                    </p>
                  </div>

                  <div className="max-w-[427px]">
                    <p className="font-['Hanken_Grotesk:Regular',sans-serif] text-[18px] xl:text-[22px] tracking-[-0.4px] leading-[1.3]">
                      <span className="font-normal text-[#525252]">
                        Além disso, também terão{" "}
                      </span>
                      <span className="font-bold text-primary">
                        descontos progressivos que podem variar de 5% até 70%
                      </span>
                      <span className="font-normal text-[#525252]">
                        , a depender da quantidade de passagens na rodovia.
                      </span>
                    </p>
                    <button
                      type="button"
                      className="mt-10 w-[180px] h-[50px] bg-brand-dark rounded-[12px] font-['Red_Hat_Display:Bold',sans-serif] font-bold text-white text-[14px] tracking-[0.3px]"
                      onClick={() => {
                        setShowPlans(true);
                        document
                          .getElementById("benefits")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      Conheça os planos
                    </button>
                  </div>
                </div>

                <div className="relative shrink-0 w-[360px] self-end xl:absolute xl:left-1/2 xl:bottom-0 xl:-translate-x-1/2 z-[1] xl:w-[50vw] xl:max-w-[700px]">
                  <img
                    src="/images/beneficios-semparar.png"
                    alt="Tag Sem Parar"
                    className="block w-full h-auto object-contain mx-auto"
                  />
                </div>
              </div>
            </>
          )}
        </section>

        {/* Principais Dúvidas */}
        <section
          id="faq"
          className="bg-white w-full pt-[67px] pb-12 md:pb-[80px]"
        >
          <div className="mx-auto px-[23px] md:px-8 md:max-w-[1286px]">
            <div className="mb-8 md:hidden">
              <p className="font-myFont text-[45px] font-black uppercase leading-[0.98] md:text-[60px]">
                <span className="text-primary">PRINCIPAIS</span>
                <br />
                <span className="text-brand-dark">DÚVIDAS</span>
              </p>
            </div>

            <div className="md:hidden md:max-w-3xl">
              <AccordionList
                items={landingMobileFaqItems}
                iconOpen="/images/faq-icon-open.svg"
                iconClosed="/images/faq-icon-closed.svg"
                iconSize={22}
                iconPosition="left"
                titleClassName="font-bold"
              />
              <div className="flex justify-center mt-8">
                <Link
                  to="/faq"
                  className="font-secondary font-bold text-base text-primary leading-[24px]"
                >
                  VER MAIS
                </Link>
              </div>
            </div>

            <div className="hidden md:block">
              <h2 className="font-['Hanken_Grotesk:Bold',sans-serif] font-bold text-[52px] xl:text-[60px] uppercase leading-[0.92] text-center text-brand-dark">
                dúvidas <span className="text-primary">principais</span>
              </h2>

              <div className="mt-[60px]">
                <AccordionList
                  items={faqInfos}
                  defaultOpenIndex={0}
                  iconOpen="/images/faq-icon-open.svg"
                  iconClosed="/images/faq-icon-closed.svg"
                  iconSize={22}
                  iconPosition="left"
                  itemClassName="mt-0"
                  buttonClassName="w-full flex items-start gap-5 text-left py-[20px] px-0 rounded-none hover:bg-transparent transition-colors duration-200"
                  titleClassName="font-['Ubuntu:Bold',sans-serif] font-bold text-[24px] leading-[34px] text-brand-dark whitespace-pre-line"
                  contentClassName="pl-[42px] pb-[20px]"
                  textClassName="font-['Ubuntu:Regular',sans-serif] text-[22px] leading-[34px] tracking-[-0.2px] text-[#505050]"
                  dividerClassName="h-px w-full bg-[#d9d9d9] mt-0 mb-0"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="w-full">
          <div className="hidden md:block relative">
            <div className="bg-brand-dark w-full px-8 py-10 lg:px-10">
              <div className="mx-auto max-w-[1366px] flex flex-col gap-8 xl:flex-row xl:items-center xl:justify-between xl:gap-14">
                <img
                  src="/images/footer-logo-semparar.svg"
                  alt="Sem Parar"
                  className="w-20 h-[67px] shrink-0"
                />

                <div className="flex-1 flex flex-col gap-8 xl:flex-row xl:items-center xl:justify-between">
                  <p className="font-secondary font-light text-xs text-[#bfbfbf] leading-[1.3] tracking-[0px] max-w-[430px]">
                    Sem Parar Instituição de Pagamento LTDA - 04.088.208/0001-65
                    <br />
                    Avenida Dra. Ruth Cardoso, nº 7221 | 05425-902 -
                    Pinheiros/SP
                  </p>

                  <div className="flex items-center flex-wrap gap-3 lg:gap-5">
                    <a
                      href="https://blog.semparar.com.br/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Blog"
                      className="bg-[#e6e6e6] rounded-[8px] size-14 flex items-center justify-center shrink-0"
                    >
                      <img
                        src="/images/footer-icon-book.svg"
                        alt=""
                        className="h-8 w-8"
                      />
                    </a>

                    <a
                      href="https://www.semparar.com.br/carreiras"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Política de privacidade"
                      className="bg-[#e6e6e6] rounded-[8px] size-14 flex items-center justify-center shrink-0"
                    >
                      <img
                        src="/images/footer-icon-luggage.svg"
                        alt=""
                        className="h-8 w-8"
                      />
                    </a>

                    <a
                      href="https://instagram.com/sempararoficial"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="bg-[#e6e6e6] rounded-[8px] size-14 flex items-center justify-center shrink-0"
                    >
                      <img
                        src="/images/footer-icon-instagram.svg"
                        alt="Instagram"
                        className="h-8 w-8"
                      />
                    </a>
                    <a
                      href="https://tiktok.com/@sempararoficial"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="TikTok"
                      className="bg-[#e6e6e6] rounded-[8px] size-14 flex items-center justify-center shrink-0"
                    >
                      <img
                        src="/images/footer-icon-tiktok.svg"
                        alt="TikTok"
                        className="h-8 w-8"
                      />
                    </a>
                    <a
                      href="https://youtube.com/sempararoficial"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="YouTube"
                      className="bg-[#e6e6e6] rounded-[8px] size-14 flex items-center justify-center shrink-0"
                    >
                      <span className="relative inline-flex h-8 w-8 items-center justify-center">
                        <img
                          src="/images/footer-icon-youtube.svg"
                          alt="YouTube"
                          className="h-8 w-8"
                        />
                      </span>
                    </a>
                    <a
                      href="https://www.facebook.com/sempararoficial/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="bg-[#e6e6e6] rounded-[8px] size-14 flex items-center justify-center shrink-0"
                    >
                      <img
                        src="/images/footer-icon-facebook.svg"
                        alt="Facebook"
                        className="h-8 w-8"
                      />
                    </a>
                    <a
                      href="https://x.com/sempararoficial"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="X"
                      className="bg-[#e6e6e6] rounded-[8px] size-14 flex items-center justify-center shrink-0"
                    >
                      <img
                        src="/images/footer-icon-x.svg"
                        alt="X"
                        className="h-8 w-8"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#363636] w-full px-8 py-5 lg:px-10">
              <div className="mx-auto max-w-[1366px] flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
                  <div className="font-secondary font-normal text-xs text-[#bfbfbf] leading-[1.3] tracking-[0px]">
                    <p>WhatsApp Atendimento:</p>
                    <p>(11) 9 8991-2822</p>
                  </div>
                  <div className="font-secondary font-normal text-xs text-[#bfbfbf] leading-[1.3] tracking-[0px]">
                    <p>Central de relacionamento</p>
                    <p>4002 1552 (Capitais)</p>
                    <p>0800 015 0252 (Demais localidades)</p>
                  </div>
                </div>

                <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
                  <div className="font-secondary font-normal text-xs text-[#bfbfbf] leading-[1.3] tracking-[0px]">
                    <p>SAC:</p>
                    <p>0800 723 2245</p>
                    <p>0800 722 0270 (deficientes auditivos)</p>
                  </div>
                  <div className="font-secondary font-normal text-xs text-[#bfbfbf] leading-[1.3] tracking-[0px]">
                    <p>Ouvidoria Sem Parar:</p>
                    <p>0800 770 0686</p>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="absolute left-1/2 -translate-x-1/2 top-[147px] bg-brand-dark rounded-b-[8px] h-8 px-8 flex items-center gap-2"
            >
              <span className="font-secondary font-light text-xs text-[#bfbfbf] leading-[1.4] tracking-[-0.4px]">
                Mapa do site
              </span>
              <img
                src="/images/footer-arrow-down.svg"
                alt=""
                className="w-4 h-[9px]"
              />
            </button>
          </div>

          <div className="md:hidden">
            <div className="bg-brand-dark w-full pt-10 pb-6">
              <div className=" mx-auto px-4 md:max-w-5xl">
                <Link
                  to="/terms"
                  className="font-secondary text-sm text-white leading-[1.3] tracking-[-0.4px] block mb-10"
                >
                  Termos e política
                </Link>

                <div className="flex gap-2 mb-10">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.semparar.semparar.minhaconta2018"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#363636] rounded-[4px] p-1 flex items-center gap-1 flex-1 h-[56px]"
                  >
                    <div className="size-8 shrink-0 flex items-center justify-center">
                      <img
                        src="/images/icon-google-play.svg"
                        alt=""
                        className="size-8"
                      />
                    </div>
                    <div className="flex flex-col justify-center text-white leading-[1.24] w-[104px]">
                      <span className="font-secondary text-xs tracking-[-0.2px]">
                        Baixar no
                      </span>
                      <span className="font-secondary font-medium text-[18px] tracking-[-0.4px] leading-tight">
                        Google Play
                      </span>
                    </div>
                  </a>
                  <a
                    href="https://apps.apple.com/br/app/sem-parar-pagar-ipva-2026/id1440651231"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#363636] rounded-[4px] p-1 flex items-center gap-1 flex-1 h-[56px]"
                  >
                    <div className="size-8 shrink-0 flex items-center justify-center">
                      <img
                        src="/images/icon-apple-store.svg"
                        alt=""
                        className="size-8"
                      />
                    </div>
                    <div className="flex flex-col justify-center text-white leading-[1.24] w-[104px]">
                      <span className="font-secondary text-xs tracking-[-0.2px]">
                        Baixar na
                      </span>
                      <span className="font-secondary font-medium text-[18px] tracking-[-0.4px] leading-tight">
                        App Store
                      </span>
                    </div>
                  </a>
                </div>

                <div className="flex gap-[22px] justify-center items-start mb-9">
                  <a
                    href="https://facebook.com/sempararoficial"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="shrink-0 bg-[#888888] rounded-full"
                  >
                    <img
                      src="/images/icon-facebook.svg"
                      alt="Facebook"
                      className="size-12 p-2"
                    />
                  </a>
                  <a
                    href="https://instagram.com/sempararoficial"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="shrink-0 bg-[#888888] rounded-full"
                  >
                    <img
                      src="/images/icon-instagram.svg"
                      alt="Instagram"
                      className="size-12 p-3"
                    />
                  </a>
                  <a
                    href="https://tiktok.com/@sempararoficial"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="TikTok"
                    className="shrink-0  bg-[#888888] rounded-full"
                  >
                    <img
                      src="/images/icon-tiktok.svg"
                      alt="TikTok"
                      className="size-12 p-2"
                    />
                  </a>
                  <a
                    href="https://x.com/sempararoficial"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="X"
                    className="shrink-0  bg-[#888888] rounded-full"
                  >
                    <img
                      src="/images/icon-x-twitter.svg"
                      alt="X"
                      className="size-12 p-3"
                    />
                  </a>
                  <a
                    href="https://youtube.com/sempararoficial"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="YouTube"
                    className="shrink-0  bg-[#888888] rounded-full"
                  >
                    <img
                      src="/images/icon-youtube.svg"
                      alt="YouTube"
                      className="size-12 p-2"
                    />
                  </a>
                </div>

                <p className="font-secondary font-light text-xs text-[#b6b6b6] tracking-[-0.4px] leading-[1.2] max-w-[267px]">
                  Sem Parar Instituição de Pagamento LTDA - 04.088.208/0001-65
                  Avenida Dra. Ruth Cardoso, nº 7221 | 05425-902 - Pinheiros/SP
                </p>
              </div>
            </div>
            <div className="bg-[#363636] w-full py-6">
              <div className="max-w-[360px] mx-auto px-4 flex flex-wrap gap-x-12 gap-y-4 md:max-w-5xl">
                <div className="font-secondary font-light text-xs text-[#b6b6b6] tracking-[-0.4px] leading-[1.4]">
                  <p>SAC:</p>
                  <p>0800 723 2245</p>
                </div>
                <div className="font-secondary font-light text-xs text-[#b6b6b6] tracking-[-0.4px] leading-[1.4]">
                  <p>Ouvidoria Sem Parar:</p>
                  <p>0800 770 0686</p>
                </div>
                <div className="font-secondary font-light text-xs text-[#b6b6b6] tracking-[-0.4px] leading-[1.4]">
                  <p>Deficiências auditivas:</p>
                  <p>0800 722 0270</p>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {isVisibleModalLogin && (
        <LoginModal toggleModalLogin={toggleModalLogin} />
      )}
      {isVisibleModal && (
        <Modal
          title={t("pages:welcome:modalTitleAttention")}
          message={getMessage()}
          actionMessage={t("common:close")}
          action={toggleModal}
          actionSecond={toggleModal}
        />
      )}
      {isLoading && <Loading />}

      <WebViewMenu
        isOpen={isWebViewMenuOpen}
        onClose={handleCloseWebViewMenu}
        onLogin={handleLoginClick}
        onCreateAccount={goToCreateAccount}
      />

      {isVisibleMenu && (
        <div className="fixed inset-0 top-[75px] md:top-[90px] xl:top-[120px] md:right-auto md:w-[50vw] md:shadow-2xl z-[1000] bg-brand-dark flex flex-col">
          <nav className="flex-1 flex flex-col justify-start px-10 pt-12">
            <p className="text-white font-secondary font-light text-sm tracking-[7px] mb-8 uppercase">
              {t("navigation:whatDoYouNeed") || "o que você precisa?"}
            </p>
            <ul className="flex flex-col gap-[35px]">
              <li>
                <button
                  onClick={() => handleMenuNavClick("about")}
                  className="text-white text-[22px] font-bold font-secondary leading-[28.8px] tracking-[-0.2px] text-left"
                >
                  {t("navigation:aboutToll")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleMenuNavClick("how-it-works")}
                  className="text-white text-[22px] font-bold font-secondary leading-[28.8px] tracking-[-0.2px] text-left"
                >
                  {t("navigation:howItWorks")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleMenuNavClick("highways")}
                  className="text-white text-[22px] font-bold font-secondary leading-[28.8px] tracking-[-0.2px] text-left"
                >
                  {t("navigation:highways")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleMenuNavClick("benefits")}
                  className="text-white text-[22px] font-bold font-secondary leading-[28.8px] tracking-[-0.2px] text-left"
                >
                  {t("navigation:tagBenefits")}
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleMenuNavClick("faq")}
                  className="text-white text-[22px] font-bold font-secondary leading-[28.8px] tracking-[-0.2px] text-left"
                >
                  {t("navigation:faq")}
                </button>
              </li>
            </ul>
            <div className="mt-16 flex flex-col gap-10">
              <button
                onClick={() => {
                  toggleMenu();
                  toggleModalLogin();
                }}
                className="text-white text-[22px] font-bold leading-tight text-left"
              >
                {t("navigation:login")}
              </button>
              <button
                onClick={() => {
                  toggleMenu();
                  goToCreateAccount();
                }}
                className="text-white text-[22px] font-bold leading-tight text-left"
              >
                {t("navigation:createAccount")}
              </button>
            </div>
          </nav>
        </div>
      )}
      {!isWebView && <WhatsAppHomeButton />}
    </div>
  );
}
